import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { BackdropCircular } from 'components';
import {
  getStoreOrdersApi,
  getStoreCustomerGroupsApi
} from 'api';
import { useStyles } from '../../orders/styles';
import CustomerOrderRowCollapsible from './CustomerOrderRowCollapsible';


const CustomerOrders = ({customer}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filterConditions = {
    customer_id: {
      field: 'customer_id',
      value: customer.id,
      conditionType: 'eq'
    }
  };

  const {
    storeOrders,
    ordersTotalCount,
    loadingOrders,
    sendingOrderEmail,
    savingStoreOrderComment,
    loadingStoreOrderInvoicePdf,
    shipOrderLoading,
    updatingStoreOrder
  } = useSelector((state: IReduxState) => state.store_orders);
  const {customerGroups } = useSelector((state: IReduxState) => state.customers);

  const loading = loadingOrders ||
    sendingOrderEmail ||
    savingStoreOrderComment ||
    loadingStoreOrderInvoicePdf ||
    shipOrderLoading ||
    updatingStoreOrder;

  useEffect(() => {
    if (customerGroups.length === 0) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  useEffect(() => {
    getStoreOrdersApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions: []
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
  ]);

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <Card elevation={20}>
      <div className="table-container">
        <TableContainer>
          <Table aria-label="collapsible table" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'increment_id'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'increment_id')}
                  >
                    <div className="px-5">Order Id</div>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'created_at'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'created_at')}
                  >
                    Purchase Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'grand_total'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'grand_total')}
                  >
                    Order Total
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'status'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell width="15%" align="center">
                  Details
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!storeOrders.length ?
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No matching orders found.
                  </TableCell>
                </TableRow>
                :
                <>
                  {storeOrders.map((order, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <CustomerOrderRowCollapsible
                        key={order.increment_id}
                        order={order}
                        labelId={labelId}
                      />
                    );
                  })}
                </>
              }
            </TableBody>
          </Table>
        </TableContainer>
        {!!ordersTotalCount &&
          <TablePagination
            rowsPerPageOptions={[10, 20, 40, 80, 100]}
            component="div"
            className={classes.paginationContainer}
            count={ordersTotalCount}
            rowsPerPage={rowsPerPage}
            page={currentPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        }
      </div>
      <BackdropCircular loading={loading} />
    </Card>
  );
};

export default CustomerOrders;
